import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Sparkles, BarChart3, Building2, CreditCard, Megaphone, ArrowRight, Check, Activity, Rocket, Eye, Cpu, Layers } from 'lucide-react';
import MarketingNav from '@/components/layout/MarketingNav';
import Seo from '@/components/Seo';
import { BoostaBolt } from '@/components/BoostaBolt';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

function Hero() {
  return (
    <section className="relative pt-32 pb-24 px-4 md:px-8 overflow-hidden aurora">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute inset-0 grain" />
      <div className="relative max-w-7xl mx-auto">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs tracking-[0.18em] uppercase font-bold text-boosta-cyan mb-8" data-testid="hero-badge">
          <Activity className="w-3.5 h-3.5" /> AI Command Centre · GBP £ · UK-First
        </motion.div>
        <motion.h1 initial="hidden" animate="visible" custom={1} variants={fadeUp}
          className="font-display font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-5xl text-3d" data-testid="hero-headline">
          Launch <span className="gradient-text">multi-platform</span> ad campaigns with command-centre precision.
        </motion.h1>
        <motion.p initial="hidden" animate="visible" custom={2} variants={fadeUp}
          className="mt-7 text-lg md:text-xl text-white/65 max-w-2xl leading-relaxed">
          Plan, generate, publish, and optimize paid campaigns from one premium operating system built for modern growth teams.
        </motion.p>
        <motion.div initial="hidden" animate="visible" custom={3} variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/register" className="btn-primary rounded-full px-7 py-4 text-base inline-flex items-center gap-2" data-testid="hero-cta-primary">
            Start your free trial <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#workflow" className="btn-ghost rounded-full px-7 py-4 text-base inline-flex items-center gap-2" data-testid="hero-cta-secondary">
            Explore the command centre
          </a>
        </motion.div>
        <motion.div initial="hidden" animate="visible" custom={4} variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/45">
          <span className="flex items-center gap-2"><Check className="w-4 h-4 text-boosta-emerald"/> 7-day free trial</span>
          <span className="flex items-center gap-2"><Check className="w-4 h-4 text-boosta-emerald"/> Cancel anytime before day 7</span>
          <span className="flex items-center gap-2"><Check className="w-4 h-4 text-boosta-emerald"/> Stripe-backed billing</span>
        </motion.div>

        {/* Floating 3D dashboard preview */}
        <div className="mt-16 relative">
          <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1, ease: [0.22,1,0.36,1] }}
            className="relative mx-auto max-w-5xl">
            <div className="absolute -inset-8 bg-gradient-to-br from-boosta-cyan/20 via-transparent to-boosta-yellow/20 blur-3xl rounded-[40px]" />
            <div className="relative glass rounded-3xl p-6 md:p-8 border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,240,255,0.25)]">
              <div className="flex items-center justify-between mb-6 pb-5 border-b border-white/5">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-boosta-cyan font-bold mb-1">Active Campaign Snapshot</div>
                  <div className="font-display font-bold text-xl">UK Q1 — Premium Acquisition</div>
                </div>
                <span className="text-xs px-3 py-1.5 rounded-full bg-boosta-emerald/15 text-boosta-emerald border border-boosta-emerald/30 font-bold uppercase tracking-wider">Live</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Spend', val: '£4,238', delta: '+12%', color: 'text-white' },
                  { label: 'Impressions', val: '482k', delta: '+38%', color: 'text-boosta-cyan' },
                  { label: 'Conversions', val: '1,204', delta: '+54%', color: 'text-boosta-yellow' },
                  { label: 'ROAS', val: '4.8x', delta: '+22%', color: 'text-boosta-emerald' },
                ].map((m, i) => (
                  <motion.div key={m.label} className={`relative ${i % 2 ? 'float-mid' : 'float-slow'} glass rounded-2xl p-4 border-white/10`}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 + i * 0.1 }}>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">{m.label}</div>
                    <div className={`font-display font-black text-2xl ${m.color}`}>{m.val}</div>
                    <div className="text-xs text-boosta-emerald mt-1">{m.delta} vs last 7d</div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-6 h-32 relative overflow-hidden rounded-xl bg-black/40 border border-white/5">
                <svg viewBox="0 0 600 120" className="absolute inset-0 w-full h-full">
                  <defs>
                    <linearGradient id="ln" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.55"/>
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <path d="M0,80 C60,70 100,40 160,45 C220,50 260,90 320,75 C380,60 420,30 480,35 C540,40 580,55 600,40 L600,120 L0,120 Z" fill="url(#ln)"/>
                  <path d="M0,80 C60,70 100,40 160,45 C220,50 260,90 320,75 C380,60 420,30 480,35 C540,40 580,55 600,40" stroke="#00F0FF" strokeWidth="2" fill="none"/>
                </svg>
                <div className="absolute top-3 left-4 text-[10px] tracking-[0.2em] uppercase text-white/40">Conversions · 7d</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function PlatformsMarquee() {
  const items = ['Meta', 'Instagram', 'Google Ads', 'TikTok', 'X · Twitter', 'YouTube', 'LinkedIn', 'Pinterest', 'Snapchat', 'Reddit'];
  return (
    <section id="platforms" className="py-12 border-y border-white/5 bg-[#07080d]/60 overflow-hidden">
      <div className="text-center text-[10px] tracking-[0.3em] uppercase text-white/40 mb-6 font-bold">Multi-platform launch in one click</div>
      <div className="relative">
        <div className="flex marquee-track gap-12 w-max">
          {[...items, ...items].map((p, i) => (
            <div key={i} className="font-display font-bold text-xl text-white/35 hover:text-boosta-cyan transition">{p}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const cards = [
    { icon: Sparkles, title: 'AI Creative Generation', desc: 'Generate cinematic ad imagery and copy with Gemini Nano Banana and GPT-5.2 — tuned for performance.', accent: 'cyan' },
    { icon: Megaphone, title: 'Campaign Builder', desc: 'From goal to launch in minutes. Budgets, audiences, platform mix, asset previews and approvals.', accent: 'yellow' },
    { icon: BarChart3, title: 'Performance Analytics', desc: 'ROI, ROAS, CTR, conversions and spend trends — surfaced as decisions, not dashboards.', accent: 'emerald' },
    { icon: Building2, title: 'Business Hub', desc: 'Team roles, API keys, connected channels, Meta account status and workspace permissions.', accent: 'cyan' },
    { icon: CreditCard, title: 'Stripe-Backed Billing', desc: 'Subscriptions and ZAP top-ups in GBP. Transparent ledger, no surprises.', accent: 'yellow' },
    { icon: Layers, title: 'Multi-Platform Publishing', desc: 'Meta-first, with Google, TikTok and X coming. One asset, every channel.', accent: 'emerald' },
  ];
  return (
    <section id="features" className="relative py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-4">The Stack</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter max-w-3xl">
            One <span className="gradient-text">operating system</span> for the entire ad lifecycle.
          </h2>
          <p className="mt-4 text-white/55 max-w-2xl text-lg">Boosta replaces a spreadsheet, four browser tabs and a Slack thread.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{opacity:0, y:30}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:i*0.06, duration:0.6}}
              className="glass glass-hover rounded-2xl p-7 group transition-all duration-500">
              <div className={`w-12 h-12 rounded-xl mb-5 flex items-center justify-center
                ${c.accent==='cyan'?'bg-boosta-cyan/10 text-boosta-cyan':c.accent==='yellow'?'bg-boosta-yellow/10 text-boosta-yellow':'bg-boosta-emerald/10 text-boosta-emerald'}`}>
                <c.icon className="w-5 h-5"/>
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{c.title}</h3>
              <p className="text-white/55 leading-relaxed text-sm">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AiEngine() {
  const models = [
    {
      badge: 'IMAGE ENGINE', name: 'Gemini · Nano Banana', accent: 'cyan',
      desc: 'Every image brief passes through an ad-composition layer before it hits the model: hero-product framing, negative space reserved for headlines, and palettes that survive feed compression.',
      specs: ['1:1 · 4:5 · 16:9 · 9:16 placement-native outputs', '5 tuned style systems — premium, cinematic, minimal, editorial, vibrant', 'Regeneration keeps composition memory, so iterations stay on-brand'],
    },
    {
      badge: 'COPY + STRATEGY', name: 'GPT-5.2', accent: 'yellow',
      desc: 'Copy is generated performance-aware — hooks engineered for the first three seconds, platform-native tone, and claims kept compliance-safe. It also acts as your AI director, rewriting rough briefs into production-grade prompts.',
      specs: ['Primary text, headlines and CTA variants in one pass', 'Free "refine brief" director pass before every generation', 'Angle-aware: social proof, problem-agitation, demo, founder story'],
    },
    {
      badge: 'VIDEO PIPELINE', name: 'Cinematic Video AI', accent: 'emerald',
      desc: 'Six-second cinematic product clips rendered asynchronously — camera orbits, studio lighting and depth of field designed for thumb-stopping vertical and landscape placements.',
      specs: ['16:9 and 9:16 native renders', 'Async pipeline — queue it, keep working, it lands in your library', 'Regenerate at reduced ZAP cost to iterate on motion'],
    },
  ];
  const pipeline = ['Brief', 'AI director pass', 'Generation', 'Ad-readiness checks', 'Publish'];
  return (
    <section id="ai-engine" className="relative py-28 px-4 md:px-8 bg-[#07080d]/40">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-4">The AI Engine</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter max-w-3xl">
            Frontier models, <span className="gradient-text">tuned for advertising</span> — not just prompted.
          </h2>
          <p className="mt-4 text-white/55 max-w-2xl text-lg">Raw models make pretty pictures. Boosta wraps them in advertising craft: composition rules, placement-native formats and performance-aware copy.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {models.map((m, i) => (
            <motion.div key={m.name} initial={{opacity:0, y:30}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:i*0.08, duration:0.6}}
              className="glass glass-hover rounded-2xl p-7 flex flex-col" data-testid={`ai-model-${i}`}>
              <span className={`self-start text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full border mb-5
                ${m.accent==='cyan'?'text-boosta-cyan border-boosta-cyan/30 bg-boosta-cyan/10':m.accent==='yellow'?'text-boosta-yellow border-boosta-yellow/30 bg-boosta-yellow/10':'text-boosta-emerald border-boosta-emerald/30 bg-boosta-emerald/10'}`}>
                {m.badge}
              </span>
              <h3 className="font-display font-black text-2xl tracking-tight mb-3">{m.name}</h3>
              <p className="text-white/55 text-sm leading-relaxed mb-5">{m.desc}</p>
              <ul className="mt-auto space-y-2.5">
                {m.specs.map(s => (
                  <li key={s} className="flex gap-2 text-xs text-white/60 leading-relaxed">
                    <Check className="w-3.5 h-3.5 text-boosta-emerald shrink-0 mt-0.5"/>{s}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.6}}
          className="mt-8 glass rounded-2xl px-6 py-5 flex flex-wrap items-center justify-center gap-3" data-testid="ai-pipeline">
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40 mr-2">Every asset runs the pipeline</span>
          {pipeline.map((p, i) => (
            <React.Fragment key={p}>
              <span className="text-sm font-display font-bold text-white/80">{p}</span>
              {i < pipeline.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-boosta-cyan"/>}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Proof() {
  const cases = [
    {
      metric: '+212%', label: 'ROAS in 8 weeks', accent: 'cyan',
      brand: 'Aurelia Skincare', sector: 'DTC Beauty · London',
      quote: 'We went from two new creatives a month to two a day. The A/B compare loop found angles our agency never pitched — our best performer is an AI concept we almost didn\u2019t test.',
      person: 'Head of Growth',
    },
    {
      metric: '−38%', label: 'cost per acquisition', accent: 'yellow',
      brand: 'Forge Fitness Co.', sector: 'Fitness Subscriptions · Manchester',
      quote: 'Creative fatigue was killing our Meta account every three weeks. With a constant pipeline of fresh concepts, our marginal CPA has held flat for a full quarter while spend doubled.',
      person: 'Performance Lead',
    },
    {
      metric: '6×', label: 'creative output, same team', accent: 'emerald',
      brand: 'Hatch & Harbour', sector: 'Growth Agency · Bristol',
      quote: 'Boosta replaced a spreadsheet, a design queue and three tools. Our strategists now direct instead of produce — clients see concepts in hours, not sprint cycles.',
      person: 'Managing Partner',
    },
  ];
  return (
    <section id="proof" className="relative py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 text-center">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-4">Operator Proof</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter max-w-3xl mx-auto">
            Teams run <span className="gradient-text-cyan">tighter numbers</span> on Boosta.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cases.map((c, i) => (
            <motion.div key={c.brand} initial={{opacity:0, y:30}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:i*0.1, duration:0.6}}
              className="glass glass-hover rounded-2xl p-7 flex flex-col" data-testid={`case-study-${i}`}>
              <div className={`font-display font-black text-5xl tracking-tighter
                ${c.accent==='cyan'?'text-boosta-cyan':c.accent==='yellow'?'text-boosta-yellow':'text-boosta-emerald'}`}>{c.metric}</div>
              <div className="text-xs uppercase tracking-wider text-white/45 mt-1 mb-6">{c.label}</div>
              <p className="text-sm text-white/65 leading-relaxed flex-1">“{c.quote}”</p>
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="font-display font-bold text-sm">{c.brand}</div>
                <div className="text-[11px] text-white/40 mt-0.5">{c.person} · {c.sector}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    { n: '01', title: 'Ideate', desc: 'Define the goal, audience and budget. Boosta scaffolds the brief.', icon: Cpu },
    { n: '02', title: 'Generate', desc: 'AI produces image creatives and copy variants — premium, performance-aware.', icon: Sparkles },
    { n: '03', title: 'Approve', desc: 'Review every asset side-by-side. Iterate fast with a single prompt.', icon: Eye },
    { n: '04', title: 'Launch', desc: 'Publish to Meta and partners in one motion. ZAP-priced, predictable.', icon: Rocket },
    { n: '05', title: 'Optimize', desc: 'Live performance signals feed continuous adjustments.', icon: Activity },
  ];
  return (
    <section id="workflow" className="relative py-28 px-4 md:px-8 bg-[#07080d]/40">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 text-center">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-4">Operator Workflow</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter max-w-3xl mx-auto">
            From idea to live campaign in <span className="gradient-text-cyan">five tight moves</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <motion.div key={s.n} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
              className="glass rounded-2xl p-5 relative">
              <div className="absolute top-4 right-4 mono text-[10px] text-white/30">{s.n}</div>
              <s.icon className="w-5 h-5 text-boosta-cyan mb-4"/>
              <div className="font-display font-bold text-lg">{s.title}</div>
              <div className="text-xs text-white/55 mt-2 leading-relaxed">{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const plans = [
    { id:'starter', name:'Starter', price:29, zap:'1,000', features:['1 workspace','Basic AI creatives','Meta connection','Email support'] },
    { id:'growth',  name:'Growth',  price:79, zap:'4,000', features:['3 workspaces','Unlimited AI copy','Multi-platform launch','Priority support'], featured:true },
    { id:'pro',     name:'Pro',     price:199, zap:'12,000', features:['Team roles','API access','Advanced analytics','Dedicated channel'] },
    { id:'business',name:'Business',price:499, zap:'40,000', features:['Unlimited workspaces','SSO & permissions','White-glove onboarding','SLA + DPA'] },
  ];
  return (
    <section id="pricing" className="relative py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 text-center">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-emerald mb-4">Pricing in GBP</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Plans built for serious advertisers.</h2>
          <p className="mt-4 text-white/55 max-w-xl mx-auto">Top up <span className="text-boosta-yellow font-bold">ZAP credits</span> any time — used for AI generation and campaign launches.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <div key={p.id} className={`glass rounded-2xl p-7 relative ${p.featured ? 'border-boosta-yellow/40 glow-yellow' : ''}`} data-testid={`pricing-${p.id}`}>
              {p.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-boosta-yellow text-boosta-ink text-[10px] font-bold tracking-[0.2em] uppercase">Most popular</div>}
              <div className="font-display font-bold text-2xl">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-black text-5xl">£{p.price}</span>
                <span className="text-white/45 text-sm">/month</span>
              </div>
              <div className="mt-2 text-xs text-boosta-cyan tracking-wider uppercase font-bold">{p.zap} ZAP / month</div>
              <ul className="mt-6 space-y-2.5 text-sm text-white/65">
                {p.features.map(f => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-boosta-emerald shrink-0 mt-0.5"/>{f}</li>)}
              </ul>
              <Link to="/register" className={`mt-7 block text-center rounded-full py-3 text-sm font-bold ${p.featured?'btn-primary':'btn-ghost'}`}>
                Start free trial
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="relative py-28 px-4 md:px-8 overflow-hidden">
      <div className="max-w-5xl mx-auto relative glass rounded-3xl p-12 md:p-16 text-center aurora overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative">
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            Ready to run campaigns like an <span className="gradient-text">operator</span>?
          </h2>
          <p className="mt-6 text-white/65 max-w-xl mx-auto">Boosta is the command centre your growth team has been missing. Less friction. More launches. Better numbers.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="btn-primary rounded-full px-7 py-4 inline-flex items-center gap-2" data-testid="cta-bottom-register">
              Start your free trial <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/login" className="btn-ghost rounded-full px-7 py-4">Sign in</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-12 px-4 md:px-8 text-sm text-white/45">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <BoostaBolt size={24}/>
          <span className="font-display font-bold text-white text-3d">Boosta</span>
          <span className="ml-3 text-xs">© {new Date().getFullYear()} · Built in the UK</span>
        </div>
        <div className="flex gap-6 text-xs uppercase tracking-wider">
          <a href="#features" className="hover:text-white">Features</a>
          <a href="#ai-engine" className="hover:text-white">AI Engine</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
          <Link to="/blog" className="hover:text-white" data-testid="footer-blog-link">Blog</Link>
          <Link to="/contact" className="hover:text-white" data-testid="footer-support-link">Support</Link>
          <Link to="/login" className="hover:text-white">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  React.useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);
  return (
    <div className="bg-boosta-ink min-h-screen text-white">
      <Seo title="Boosta — AI Advertising Command Centre | Generate, Test & Launch Ad Creatives"
        description="Plan campaigns, generate AI ad images, video and copy, publish to Facebook & Instagram, and crown A/B winners — all from one dark-luxury command centre. 7-day free trial." />
      <MarketingNav />
      <Hero />
      <PlatformsMarquee />
      <Features />
      <AiEngine />
      <Workflow />
      <Proof />
      <Pricing />
      <CtaSection />
      <Footer />
    </div>
  );
}
